// Authentication service for the UK Government Data Portal

// API URL (defaults to localhost for development)
import config from '../config';

const API_URL = config.apiUrl;

// Local storage keys
const ACCESS_TOKEN_KEY = 'whatgov_access_token';
const REFRESH_TOKEN_KEY = 'whatgov_refresh_token';
const USER_KEY = 'whatgov_user';
const EXPIRES_AT_KEY = 'whatgov_expires_at';

// Exchange authorization code for token (used in redirect flow)
export const exchangeCodeForToken = async (code) => {
  try {
    if (!code) {
      console.error('No authorization code provided');
      return {
        isAuthenticated: false,
        error: 'No authorization code provided'
      };
    }

    console.log('Exchanging code for token');
    
    // For offline mode testing
    if (window.navigator && !window.navigator.onLine) {
      console.log('Offline mode detected, simulating successful code exchange');
      
      // Extract user info from the code if possible (for demo purposes)
      // In a real implementation, the backend would validate the code with Google
      let name = 'Google User';
      let email = 'user@example.com';
      
      // Try to extract some info from the code for a more realistic demo
      try {
        // Use part of the code as a seed for generating consistent mock data
        const codeSeed = code.substring(0, 8);
        // This is just for demo - in reality, the code doesn't contain user info
        name = `User ${codeSeed}`;
        email = `user-${codeSeed.toLowerCase()}@example.com`;
      } catch (e) {
        console.log('Could not extract demo info from code, using defaults');
      }
      
      const mockUser = {
        id: 'mock-user-id',
        name: name,
        email: email,
        pictureUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`
      };
      
      // Store the mock token and user info
      localStorage.setItem(ACCESS_TOKEN_KEY, 'mock-jwt-token');
      localStorage.setItem(USER_KEY, JSON.stringify(mockUser));
      localStorage.setItem(EXPIRES_AT_KEY, (Date.now() + 3600 * 1000).toString());
      
      return {
        isAuthenticated: true,
        user: mockUser
      };
    }
    
    // In a real implementation, send the code to your backend
    const redirectUri = `${window.location.origin}/auth/callback`;
    
    // Using Connect protocol format
    const response = await fetch(`${API_URL}/whatgov.v1.WhatGovService/ExchangeGoogleCode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ 
        code,
        redirect_uri: redirectUri  // Use snake_case for protocol buffer field names
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error exchanging code:', errorText);
      return {
        isAuthenticated: false,
        error: `Server error: ${response.status} ${errorText}`
      };
    }
    
    const data = await response.json();
    
    if (data.token && data.user) {
      // Store the token and user info
      localStorage.setItem(ACCESS_TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.setItem(EXPIRES_AT_KEY, (Date.now() + data.expiresIn * 1000).toString());
      
      return {
        isAuthenticated: true,
        user: data.user
      };
    } else {
      console.error('Invalid response from server:', data);
      return {
        isAuthenticated: false,
        error: data.error || 'Invalid response from server'
      };
    }
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    return {
      isAuthenticated: false,
      error: error.message || 'Failed to exchange code for token'
    };
  }
};

// Legacy Google Sign-In (for backward compatibility)
export const googleSignIn = async (idToken, decodedUserInfo = null) => {
  try {
    if (!idToken) {
      console.error('No ID token provided for Google Sign-In');
      return {
        user: null,
        isAuthenticated: false,
        error: 'No authentication token received from Google'
      };
    }
    
    console.log('Sending Google token to backend for verification');
    console.log('Token length:', idToken.length);
    console.log('API URL:', API_URL);
    
    // For testing in offline/airplane mode, we can simulate a successful login
    // This is a temporary solution for development purposes
    if (window.navigator && !window.navigator.onLine) { // Changed back to only use offline mode when actually offline
      console.log('Offline mode detected, simulating successful login');
      
      // If we have decoded user info from Google, use it instead of mock data
      const mockUser = decodedUserInfo ? {
        id: decodedUserInfo.sub || 'mock-user-id',
        name: decodedUserInfo.name || 'Google User',
        email: decodedUserInfo.email || 'user@example.com',
        pictureUrl: decodedUserInfo.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(decodedUserInfo.name || 'User')}&background=random`
      } : {
        id: 'mock-user-id',
        name: 'Test User',
        email: 'test@example.com',
        pictureUrl: 'https://ui-avatars.com/api/?name=Test+User&background=random'
      };
      
      const now = Date.now();
      const expiresAt = now + (60 * 60 * 1000); // 1 hour from now
      
      // Store mock authentication data
      localStorage.setItem(ACCESS_TOKEN_KEY, 'mock-access-token');
      localStorage.setItem(REFRESH_TOKEN_KEY, 'mock-refresh-token');
      localStorage.setItem(USER_KEY, JSON.stringify(mockUser));
      localStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
      
      return {
        user: mockUser,
        isAuthenticated: true
      };
    }
    
    try {
      const response = await fetch(`${API_URL}/whatgov.v1.WhatGovService/GoogleSignIn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Connect-Protocol-Version': '1'
        },
        body: JSON.stringify({ idToken }),
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Sign-in successful, storing tokens');

      // Store authentication data
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.setItem(EXPIRES_AT_KEY, data.expiresAt);

      return {
        user: data.user,
        isAuthenticated: true
      };
    } catch (fetchError) {
      console.error('Fetch operation failed:', fetchError);
      throw fetchError; // Re-throw to be caught by the outer try-catch
    }
  } catch (error) {
    console.error('Google Sign-In failed:', error);
    return {
      user: null,
      isAuthenticated: false,
      error: error.message
    };
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  
  if (!accessToken || !expiresAt) {
    return false;
  }
  
  // Check if token is expired
  return parseInt(expiresAt, 10) > Date.now();
};

// Get current user
export const getCurrentUser = () => {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) {
    return null;
  }
  
  try {
    return JSON.parse(userJson);
  } catch (error) {
    console.error('Failed to parse user data', error);
    return null;
  }
};

// Logout
export const logout = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  
  if (refreshToken) {
    try {
      await fetch(`${API_URL}/whatgov.v1.WhatGovService/Logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
  }
  
  // Clear local storage regardless of API call success
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  
  return {
    user: null,
    isAuthenticated: false
  };
};
